/**
 * Converts quiz question/option/explanation plain text or HTML into safe structured HTML
 * with C-like syntax highlighting. See Quiz_Question_Formatting.md.
 */

const C_KEYWORDS = new Set([
    'auto', 'break', 'case', 'char', 'const', 'continue', 'default', 'do', 'double',
    'else', 'enum', 'extern', 'float', 'for', 'goto', 'if', 'int', 'long', 'printf',
    'register', 'return', 'scanf', 'short', 'signed', 'sizeof', 'static', 'struct',
    'switch', 'typedef', 'union', 'unsigned', 'void', 'volatile', 'while', 'define',
    'include', 'main'
]);

const C_CODE_START_RE = /^(#include|#define|\b(auto|break|case|char|const|continue|default|do|double|else|enum|extern|float|for|goto|if|int|long|printf|register|return|scanf|short|signed|sizeof|static|struct|switch|typedef|union|unsigned|void|volatile|while|main)\b|[a-zA-Z_][\w]*\s*\()/;

const HTML_TAG_RE = /<\s*\/?\s*(p|pre|code|div|br|span|strong|em|ul|ol|li|h[1-6])\b/i;

const FENCE_RE = /```[\w]*\n?([\s\S]*?)```/g;

const INLINE_PHRASES = [
    'array of characters',
    'array of int',
    'pointer to char'
];

const STATEMENT_START_RE = /\b(char|int|float|double|void|short|long|unsigned|signed|static|const|struct|enum|typedef)\b/;

/**
 * @param {string} text
 * @returns {string}
 */
export function escapeHtml(text) {
    if (text == null) return '';
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * @param {string} code
 * @returns {string}
 */
export function highlightCLike(code) {
    if (!code) return '';
    let s = escapeHtml(code);

    // Strings (simple: non-greedy, no escaped quotes inside)
    s = s.replace(/('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")/g, '<span class="quiz-syn-str">$1</span>');

    // Comments
    s = s.replace(/(\/\/[^\n]*)/g, '<span class="quiz-syn-comment">$1</span>');

    // #include / #define lines
    s = s.replace(/(#include\s*&lt;[^&]+&gt;|#define\s+[^\n]+)/g, '<span class="quiz-syn-pre">$1</span>');

    // Keywords (word boundaries, avoid inside existing spans)
    const parts = s.split(/(<span class="quiz-syn-[^"]*">[\s\S]*?<\/span>)/g);
    s = parts.map((part) => {
        if (part.startsWith('<span class="quiz-syn-')) return part;
        return part.replace(/\b([a-zA-Z_][\w]*)\b/g, (match) => {
            if (C_KEYWORDS.has(match)) {
                return `<span class="quiz-syn-kw">${match}</span>`;
            }
            return match;
        });
    }).join('');

    // Numbers (outside spans — second pass on non-span segments)
    const parts2 = s.split(/(<span class="quiz-syn-[^"]*">[\s\S]*?<\/span>)/g);
    s = parts2.map((part) => {
        if (part.startsWith('<span class="quiz-syn-')) return part;
        return part.replace(/\b(\d+\.?\d*)\b/g, '<span class="quiz-syn-num">$1</span>');
    }).join('');

    return s;
}

/**
 * @param {string} line
 * @returns {boolean}
 */
export function looksLikeCCode(line) {
    const trimmed = (line || '').trim();
    if (!trimmed) return false;
    if (!C_CODE_START_RE.test(trimmed)) return false;
    return trimmed.includes(';');
}

/**
 * @param {string} code
 * @returns {string}
 */
export function stripCodeWrappingQuotes(code) {
    let t = (code || '').trim();
    if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
        return t.slice(1, -1).trim();
    }
    // Orphan wrapper quote when opening quote was consumed elsewhere
    if (!t.startsWith("'") && t.endsWith("'") && /;\s*'\s*$/.test(t)) {
        t = t.slice(0, -1).trim();
    }
    if (t.startsWith("'") && !t.endsWith("'") && /^'(?:\s*(?:char|int|void|float|double|#include|#define)\b)/.test(t)) {
        t = t.slice(1).trim();
    }
    return t;
}

/**
 * @param {string} code
 * @returns {string}
 */
export function layoutCodeBlock(code) {
    const cleaned = stripCodeWrappingQuotes(code);
    const statementStarts = /\s*(?=(#include|#define|\b(char|int|float|double|void|short|long|unsigned|signed|static|const|struct|enum|typedef|printf|scanf|for|while|if|switch|return)\b))/g;
    return cleaned.replace(/;(?=\s*(?=#include|#define|\b(char|int|float|double|void|short|long|unsigned|signed|static|const|struct|enum|typedef|printf|scanf|for|while|if|switch|return)\b))/g, ';\n');
}

/**
 * @param {string} line
 * @returns {boolean}
 */
export function isCodeLikeLine(line) {
    const trimmed = (line || '').trim();
    if (!trimmed) return false;
    if (/^#include\s*</.test(trimmed) || /^#define\b/.test(trimmed)) return true;
    if (trimmed.endsWith(';') && /\b(int|char|void|float|double|short|long|unsigned|signed|static|const|struct|enum|typedef|return|printf|scanf)\b/.test(trimmed)) {
        return true;
    }
    if (/^[a-zA-Z_][\w]*\s*\([^)]*\)\s*(\{|;)?\s*$/.test(trimmed)) return true;
    if (/^[a-zA-Z_][\w]*\s*\(/.test(trimmed) && trimmed.includes('{')) return true;
    if (looksLikeCCode(trimmed) && trimmed.length < 200) return true;
    return false;
}

/**
 * @param {string} line
 * @returns {{ prose: string, code: string } | null}
 */
export function splitProseAndCode(line) {
    const trimmed = (line || '').trim();
    if (!trimmed) return null;
    if (isCodeLikeLine(trimmed)) return null;

    const qMatch = trimmed.match(/^(.+?\?)\s*(.+)$/);
    if (qMatch) {
        const code = stripCodeWrappingQuotes(qMatch[2]);
        if (looksLikeCCode(code)) {
            return { prose: qMatch[1].trim(), code };
        }
    }

    const colonMatch = trimmed.match(/^(.+?:)\s*(.+)$/);
    if (colonMatch) {
        const code = stripCodeWrappingQuotes(colonMatch[2]);
        if (looksLikeCCode(code)) {
            return { prose: colonMatch[1].trim(), code };
        }
    }

    const tokenRe = /\b(#include|#define|char|int|float|double|void|short|long|unsigned|signed|static|const|struct|enum|typedef|printf|scanf)\b/;
    const searchFrom = Math.min(8, trimmed.length);
    const slice = trimmed.slice(searchFrom);
    const tokenMatch = slice.match(tokenRe);
    if (tokenMatch && tokenMatch.index != null) {
        const idx = searchFrom + tokenMatch.index;
        const prose = trimmed.slice(0, idx).trim();
        const code = trimmed.slice(idx).trim();
        if (prose && looksLikeCCode(code)) {
            return { prose, code: stripCodeWrappingQuotes(code) };
        }
    }

    return null;
}

/**
 * @param {string} line
 * @returns {string}
 */
export function formatInlineText(line) {
    let s = escapeHtml(line || '');

    s = s.replace(/`([^`]+)`/g, (_, inner) => {
        return `<code class="quiz-inline-code">${highlightCLike(inner)}</code>`;
    });

    s = s.replace(/(\b(?:char|int|float|double|void|short|long|unsigned|signed|static|const|struct|enum|typedef)\b[^;]{0,120};)/g, (match) => {
        if (match.includes('quiz-inline-code')) return match;
        return `<code class="quiz-inline-code">${highlightCLike(match)}</code>`;
    });

    s = s.replace(/\b(char\s+\w+\s*\[\s*\]|int\s*\*\s*\w+|char\s*\*\s*\w+)\b/gi, (match) => {
        if (match.includes('quiz-inline-code') || match.includes('quiz-syn-')) return match;
        return `<code class="quiz-inline-code">${highlightCLike(match)}</code>`;
    });

    for (const phrase of INLINE_PHRASES) {
        const re = new RegExp(`\\b(${phrase.replace(/\s+/g, '\\s+')})\\b`, 'gi');
        s = s.replace(re, (match) => {
            if (match.includes('quiz-inline-code') || match.includes('quiz-syn-')) return match;
            return `<code class="quiz-inline-code">${highlightCLike(match)}</code>`;
        });
    }

    return s;
}

/**
 * @param {string} code
 * @returns {string}
 */
function codeBlockHtml(code) {
    const laid = layoutCodeBlock(code);
    const highlighted = highlightCLike(laid);
    return `<pre class="quiz-code-block"><code>${highlighted}</code></pre>`;
}

/**
 * @param {string} textSegment
 * @returns {string}
 */
function processPlainTextSegment(textSegment) {
    const lines = textSegment.split('\n');
    const parts = [];

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
            parts.push('<p class="quiz-prose">&nbsp;</p>');
            continue;
        }

        const split = splitProseAndCode(line);
        if (split) {
            parts.push(`<p class="quiz-prose">${formatInlineText(split.prose)}</p>`);
            parts.push(codeBlockHtml(split.code));
            continue;
        }

        if (isCodeLikeLine(trimmed)) {
            parts.push(codeBlockHtml(trimmed));
            continue;
        }

        parts.push(`<p class="quiz-prose">${formatInlineText(line)}</p>`);
    }

    return parts.join('');
}

/**
 * @param {string} raw
 * @returns {string}
 */
export function enrichExistingHtml(raw) {
    let html = String(raw || '');
    html = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');

    html = html.replace(/<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi, (_, code) => {
        const decoded = code
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"');
        return codeBlockHtml(decoded);
    });

    html = html.replace(/<code([^>]*)>([\s\S]*?)<\/code>/gi, (full, attrs, code) => {
        if (full.includes('quiz-code-block') || full.includes('quiz-inline-code')) return full;
        const decoded = code
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"');
        if (decoded.includes('\n') || isCodeLikeLine(decoded)) {
            return codeBlockHtml(decoded);
        }
        return `<code class="quiz-inline-code">${highlightCLike(decoded)}</code>`;
    });

    html = html.replace(/<p([^>]*)>/gi, '<p class="quiz-prose"$1>');
    if (!html.includes('quiz-content-html')) {
        html = `<div class="quiz-content-html">${html}</div>`;
    }
    return html;
}

function hasHtmlTags(text) {
    return HTML_TAG_RE.test(text || '');
}

/**
 * @param {string} raw
 * @returns {string}
 */
export function buildQuizContentHtml(raw) {
    if (raw == null || String(raw).trim() === '') return '';

    const text = String(raw);
    if (hasHtmlTags(text)) {
        return enrichExistingHtml(text);
    }

    const segments = [];
    let lastIndex = 0;
    const fenceCopy = text.replace(/\r\n/g, '\n');
    FENCE_RE.lastIndex = 0;
    let match;
    const fenceRegex = /```[\w]*\n?([\s\S]*?)```/g;

    while ((match = fenceRegex.exec(fenceCopy)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: fenceCopy.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'code', value: match[1] || '' });
        lastIndex = fenceRegex.lastIndex;
    }
    if (lastIndex < fenceCopy.length) {
        segments.push({ type: 'text', value: fenceCopy.slice(lastIndex) });
    }
    if (segments.length === 0) {
        segments.push({ type: 'text', value: fenceCopy });
    }

    let body = '';
    for (const seg of segments) {
        if (seg.type === 'code') {
            body += codeBlockHtml(seg.value.trim());
        } else {
            body += processPlainTextSegment(seg.value);
        }
    }

    return `<div class="quiz-content-html">${body}</div>`;
}

export default buildQuizContentHtml;
